/**
 * Pure 30-day trend-insight generation for the Health Trends view.
 * Returns the array of insight strings; the container renders them.
 */
import type { ChartData } from './aggregate';

/** Build the 30-day trend insight strings from chart data + raw items (for calorie target). */
export function buildInsights(chartData: ChartData, allItems: any[]): string[] {
  const insights: string[] = [];
  const weightPts = chartData['weight'];
  if (weightPts && weightPts.length >= 7) {
    const sorted = [...weightPts].sort((a, b) => a.date.localeCompare(b.date));
    const first = sorted[0].value;
    const last = sorted[sorted.length - 1].value;
    const diff = last - first;
    if (Math.abs(diff) >= 0.5) {
      insights.push(`Weight ${diff < 0 ? 'down' : 'up'} ${Math.abs(diff).toFixed(1)} lbs over ${sorted.length} days (${first.toFixed(1)} → ${last.toFixed(1)}).${diff < 0 ? ' Good progress — keep it steady.' : ' Consider reviewing calorie targets.'}`);
    } else {
      insights.push(`Weight stable around ${last.toFixed(1)} lbs over ${sorted.length} days.`);
    }
  }
  const calPts = chartData['calories'];
  if (calPts && calPts.length >= 7) {
    const profileStat = allItems.find((s: any) => (s.source || s.attributes?.source) === 'health:profile');
    const calTarget = profileStat?.dailyCalorieTarget || profileStat?.calorieTarget || profileStat?.attributes?.dailyCalorieTarget || profileStat?.attributes?.calorieTarget || 1800;
    const avg = Math.round(calPts.reduce((s, p) => s + p.value, 0) / calPts.length);
    const overDays = calPts.filter(p => p.value > calTarget).length;
    insights.push(`Averaging ${avg.toLocaleString()} cal/day (target: ${calTarget.toLocaleString()}). ${overDays > calPts.length * 0.5 ? `Over target ${overDays} of ${calPts.length} days — watch portion sizes.` : `On target most days — nice discipline.`}`);
  }
  const exPts = chartData['exercise'];
  if (exPts && exPts.length >= 3) {
    const activeDays = exPts.filter(p => p.value > 0).length;
    const avgBurn = Math.round(exPts.reduce((s, p) => s + p.value, 0) / exPts.length);
    insights.push(`Active ${activeDays} of the last 30 days, averaging ${avgBurn} cal burned per session.${activeDays >= 20 ? ' Excellent consistency.' : activeDays >= 12 ? ' Solid routine.' : ' Try to add 1-2 more sessions per week.'}`);
  }
  const bpPts = chartData['systolic'];
  if (bpPts && bpPts.length >= 7) {
    const avgSys = Math.round(bpPts.reduce((s, p) => s + p.value, 0) / bpPts.length);
    insights.push(`Average systolic BP: ${avgSys} mmHg.${avgSys < 120 ? ' Optimal range.' : avgSys < 130 ? ' Elevated — monitor closely.' : ' High — consult your doctor.'}`);
  }
  return insights;
}
